import React, { useState } from 'react';
import PageSeo from '../components/PageSeo';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BuildIcon from '@mui/icons-material/Build';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import MemoryIcon from '@mui/icons-material/Memory';
import SupportIcon from '@mui/icons-material/Support';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BrandHeroEyebrow from '../components/BrandHeroEyebrow';
import { brandHeroSection, heroContentZ } from '../theme/heroStyles';

const ServicesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);
  const [highlightedService, setHighlightedService] = useState(null);

  // Handle hash navigation and highlight
  React.useEffect(() => {
    if (window.location.hash) {
      const serviceId = window.location.hash.substring(1);
      setHighlightedService(serviceId);
      
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedService(null);
      }, 3000);
    }
  }, []);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const services = [
    {
      id: 'diesel-pump-repair',
      icon: <BuildIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Diesel Pump Repairs & Maintenance',
      description: 'Expert repair and maintenance services for diesel pumps, injectors and turbochargers.',
      features: [
        'Fuel Injection Pump Repair',
        'Injector Maintenance',
        'Turbocharger Service',
        'High-Pressure Pump Testing',
        'Calibration Services',
        'Performance Optimization'
      ],
      image: '/assets/Diesel and Pump Repairs.jpeg',
      category: 'Repair'
    },
    {
      id: 'engine-overhaul',
      icon: <EngineeringIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Engine Overhauls',
      description: 'Complete engine overhaul services for all makes of diesel vehicles and machinery.',
      features: [
        'Complete Engine Rebuilds',
        'Cylinder Head Service',
        'Crankshaft Repair',
        'Piston & Ring Replacement',
        'Bearing Replacement',
        'Engine Testing & Validation'
      ],
      image: '/assets/Engine Overhauls.jpeg',
      category: 'Overhaul'
    },
    {
      id: 'injection-system-service',
      icon: <SettingsIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Diesel Injection Services',
      description: 'Comprehensive servicing of diesel vehicle injection systems with modern diagnostic equipment.',
      features: [
        'Electronic Injection Systems',
        'Mechanical Injection Systems',
        'Common Rail Systems',
        'System Diagnostics',
        'Performance Testing',
        'Preventive Maintenance'
      ],
      image: '/assets/Diesel Injection Services .jpeg',
      category: 'Service'
    },
    {
      id: 'general-diesel-service',
      icon: <SupportIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'General Diesel Vehicle Service',
      description: 'Complete servicing of diesel vehicles with attention to fuel system components.',
      features: [
        'Oil & Filter Changes',
        'Air Filter Service',
        'Brake System Service',
        'Suspension Maintenance',
        'Complete Vehicle Inspection'
      ],
      image: '/assets/General Diesel Vehicle Service.jpeg',
      category: 'Service'
    },
    {
      id: 'electronic-diagnostics',
      icon: <MemoryIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Electronic Diagnostic & Repairs',
      description: 'Advanced electronic diagnostic troubleshooting and repair services for modern diesel systems.',
      features: [
        'ECU Diagnostics',
        'Fault Code Reading',
        'Sensor Testing',
        'Wiring Repairs',
        'System Programming',
        'Performance Tuning'
      ],
      image: '/assets/Electronic diagnostioc & repairs.jpeg',
      category: 'Diagnostics'
    },
    {
      id: 'injector-coding',
      icon: <SpeedIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Injector Coding',
      description: 'Professional injector coding services for all electronic diesel injection systems.',
      features: [
        'Injector Calibration',
        'Flow Rate Testing',
        'Performance Validation',
        'Warranty Support'
      ],
      image: '/assets/Injector Coding.jpeg',
      category: 'Coding'
    },
    {
      id: 'injectors-turbo-chargers',
      icon: <BuildIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Injectors and Turbo Chargers',
      description: 'Expert repair, maintenance, and replacement services for injectors and turbochargers.',
      features: [
        'Injector Repair & Testing',
        'Turbocharger Rebuild',
        'Turbocharger Replacement',
        'Performance Upgrades',
        'Diagnostic Testing',
        'Warranty Support'
      ],
      image: '/assets/Injectors and Turbo Chargers.jpeg',
      category: 'Repair'
    }
  ];

  const categories = ['All', 'Repair', 'Overhaul', 'Service', 'Diagnostics', 'Coding'];

  return (
    <>
      <PageSeo
        pathname="/services"
        title="Diesel Services | Pump Repair, Overhauls & Diagnostics | Diesel City"
        description="Diesel pump and injector repair, engine overhauls, injection systems, ECU diagnostics, injector coding and turbo service — all makes, Johannesburg."
        ogTitle="Our Diesel Services"
        ogDescription="Pump repair, engine overhauls, diagnostics and turbo service for commercial vehicles and earth-moving equipment."
      />

      <Box
        sx={{
          ...brandHeroSection(theme, '/assets/engineReplace.jpeg'),
        }}
      >
        <Container maxWidth="lg" sx={heroContentZ}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <BrandHeroEyebrow label="Our services" />
              </Box>
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                gutterBottom
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: 1.1,
                  mb: 3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                Expert Diesel
                <Box component="span" sx={{ 
                  color: 'secondary.main', 
                  display: 'block',
                  mt: 1
                }}>
                  Services
                </Box>
              </Typography>
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{ 
                  mb: 4, 
                  opacity: 0.95, 
                  lineHeight: 1.7,
                  maxWidth: 500
                }}
              >
                We offer comprehensive diesel services including pump repair, engine overhauls, 
                diesel injection services, and diagnostics for all makes of vehicles and machinery.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: { xs: 300, md: 500 },
                  position: 'relative'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: 500,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    border: '4px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/engineReplace.jpeg"
                    alt="Diesel City Services"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 2,
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Overview */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              What We Offer
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'text.secondary', 
                maxWidth: 700, 
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              From pump repair to engine overhauls, we provide expert diesel services for all your needs
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} lg={4} key={service.id}>
                <Card
                  id={service.id}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: highlightedService === service.id 
                      ? '0 0 0 3px rgba(25, 118, 210, 0.5), 0 4px 20px rgba(0,0,0,0.08)'
                      : '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: highlightedService === service.id ? 'primary.main' : 'divider',
                    overflow: 'hidden',
                    transform: highlightedService === service.id ? 'scale(1.02)' : 'none',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease-in-out',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      backgroundColor: 'background.default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component="img"
                      src={service.image}
                      alt={service.title}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        width: 'auto',
                        height: 'auto'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {service.icon}
                        <Chip 
                          label={service.category} 
                          size="small"
                          sx={{ 
                            backgroundColor: 'primary.light',
                            color: 'white',
                            fontWeight: 500
                          }} 
                        />
                      </Box>
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 700,
                          mb: 2,
                          fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 3,
                          lineHeight: 1.6,
                          fontSize: { xs: '0.875rem', md: '1rem' }
                        }}
                      >
                        {service.description}
                      </Typography>

                      <Accordion
                        expanded={expanded === service.id}
                        onChange={handleChange(service.id)}
                        disableGutters
                        sx={{
                          boxShadow: 'none',
                          border: 'none',
                          bgcolor: 'transparent',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': { margin: 0 },
                          '& .MuiAccordionDetails-root': {
                            textAlign: 'left',
                            display: 'block',
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            px: 1.5,
                            py: 1.25,
                            minHeight: 0,
                            borderRadius: 2,
                            bgcolor: 'action.hover',
                            '&:hover': { bgcolor: 'action.selected' },
                            '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
                            '& .MuiAccordionSummary-expandIconWrapper': { color: 'primary.main' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 600,
                            }}
                          >
                            View Features
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, pt: 2, pb: 0.5, textAlign: 'left' }}>
                          <Stack spacing={1} sx={{ alignItems: 'flex-start', width: '100%' }}>
                            {service.features.map((feature, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', justifyContent: 'flex-start' }}>
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, flexShrink: 0, mt: 0.25 }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', flex: 1 }}>
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    </Box>

                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      fullWidth
                      sx={{
                        mt: 'auto',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'secondary.contrastText',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/assets/IMG-20250819-WA0037.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 5, 
                opacity: 0.95,
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Let's discuss how our services can help transform your business and drive success
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(23, 28, 143, 0.4)',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(23, 28, 143, 0.6)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              Contact Us Today
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ServicesPage;
